import { useEffect, useRef, useState } from "react";
import { ArrowDown, Languages } from "lucide-react";

import { cn } from "../../../lib/utils";
import { alertLabel } from "../alert-badge";
import { readCut } from "../lib/speech-cut";
import { transcriptText, type TranscriptLanguageMode } from "../lib/transcript-language";
import type { Alert, TranscriptMessage } from "../types";
import { SpeechCutBody, SpeechCutMeter } from "./speech-cut";

export function TranscriptView({
  messages,
  alerts,
  live = true,
  languageMode = "both",
  onLanguageModeChange,
}: {
  messages: TranscriptMessage[];
  alerts: Alert[];
  live?: boolean;
  languageMode?: TranscriptLanguageMode;
  onLanguageModeChange?: (mode: TranscriptLanguageMode) => void;
}) {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const previousCount = useRef(0);
  const atBottom = useRef(true);
  const [unseen, setUnseen] = useState(0);

  const scrollToLatest = (behavior: ScrollBehavior = "smooth") => {
    const node = scrollRef.current;
    if (!node) return;
    if (typeof node.scrollTo === "function") node.scrollTo({top: node.scrollHeight, behavior});
    else node.scrollTop = node.scrollHeight;
    atBottom.current = true;
    setUnseen(0);
  };

  useEffect(() => {
    const added = Math.max(0, messages.length - previousCount.current);
    previousCount.current = messages.length;
    if (!live || !added) return;
    if (atBottom.current) {
      requestAnimationFrame(() => scrollToLatest("auto"));
    } else {
      setUnseen((current) => current + added);
    }
  }, [messages.length, live]);

  const evidence = new Map<string, Alert>();
  for (const alert of alerts) {
    if (alert.item_id && alert.status !== "false_positive") evidence.set(alert.item_id, alert);
  }

  if (!messages.length) {
    return <div className="flex min-h-0 flex-1 items-center justify-center p-5 text-sm text-ink-3">No transcript yet.</div>;
  }

  return (
    <div className="relative min-h-0 flex-1 bg-canvas">
      <div
        ref={scrollRef}
        className="h-full overflow-y-auto px-4 py-3"
        onScroll={(event) => {
          const node = event.currentTarget;
          const nearBottom = node.scrollHeight - node.scrollTop - node.clientHeight < 64;
          atBottom.current = nearBottom;
          if (nearBottom) setUnseen(0);
        }}
      >
        <div className="sticky top-0 z-10 mb-2 flex items-center gap-2 bg-canvas py-1">
          <span className="text-[9px] font-semibold uppercase tracking-[0.14em] text-ink-3">{live ? "Live transcript" : "Transcript"}</span>
          <span className="h-px flex-1 bg-line" />
          {onLanguageModeChange ? (
            <span className="flex items-center rounded-md border border-line bg-soft p-0.5" aria-label="Transcript language">
              <Languages size={12} className="mx-1 text-ink-3" />
              {(["both", "hindi", "english"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  aria-pressed={languageMode === mode}
                  title={mode === "english" ? "English letters" : mode === "hindi" ? "Hindi only" : "Hindi and English letters"}
                  onClick={() => onLanguageModeChange(mode)}
                  className={cn(
                    "rounded px-1.5 py-0.5 text-[9px] font-medium capitalize transition",
                    languageMode === mode ? "bg-elevated text-ink shadow-sm" : "text-ink-3 hover:text-ink",
                  )}
                >
                  {mode}
                </button>
              ))}
            </span>
          ) : null}
        </div>
        <div className="space-y-1">
          {messages.map((message, index) => {
            const flagged = message.item_id ? evidence.get(message.item_id) : undefined;
            const cut = readCut(message);
            // A cut turn's `text` is what the model generated. SpeechCutBody owns
            // the rule that the body shows only what was voiced; the meter below
            // carries the never-voiced tail.
            const text = cut.visible
              ? transcriptText(cut.spoken, languageMode)
              : transcriptText(message.text, languageMode);
            const role = message.held ? "--" : message.role === "assistant" ? "AI" : "CU";

            return (
              <div
                key={message.item_id || index}
                id={message.item_id ? `transcript-turn-${encodeURIComponent(message.item_id)}` : undefined}
                data-testid={`supervision-turn-${message.item_id || index}`}
                data-transcript-item-id={message.item_id}
                data-transcript-index={index}
                className={cn(
                  "grid grid-cols-[30px_minmax(0,1fr)] gap-2 rounded-lg border-l-2 px-2 py-2 text-sm",
                  message.held
                    ? "border-warning bg-warning-soft/50"
                    : flagged?.severity === "critical"
                      ? "border-danger bg-danger-soft/40"
                      : flagged
                        ? "border-warning bg-warning-soft/35"
                        : "border-transparent hover:bg-soft/60",
                )}
              >
                <span
                  className={cn(
                    "pt-0.5 font-mono text-[10px] font-semibold",
                    message.held ? "text-warning" : message.role === "assistant" ? "text-brand" : "text-ink-3",
                  )}
                  title={message.held ? "Said during takeover — speaker not identified" : undefined}
                >
                  {role}
                  <span className="sr-only">
                    {message.held ? "During takeover · speaker not identified" : message.role === "assistant" ? "AI agent" : "Caller"}
                  </span>
                </span>
                <div className="min-w-0">
                  <SpeechCutBody
                    message={message}
                    displayed={text.primary}
                    className="whitespace-pre-wrap break-words leading-5 text-ink"
                  />
                  {text.secondary ? <p className="mt-0.5 break-words text-[11px] italic leading-4 text-ink-3">{text.secondary}</p> : null}
                  <SpeechCutMeter message={message} />
                  {flagged ? (
                    <span className={cn(
                      "mt-1 inline-flex rounded px-1.5 py-0.5 text-[9px] font-medium",
                      flagged.severity === "critical" ? "bg-danger-soft text-danger" : "bg-warning-soft text-warning",
                    )}>
                      {alertLabel(flagged)}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {unseen ? (
        <button
          type="button"
          onClick={() => scrollToLatest()}
          className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-line bg-elevated px-3 py-1.5 text-[11px] font-medium text-brand shadow-lg"
        >
          <ArrowDown size={12} /> {unseen} new {unseen === 1 ? "turn" : "turns"}
        </button>
      ) : null}
    </div>
  );
}
